import clsx from 'clsx';
import { ReactEventHandler, useState } from 'react';
import ColorThief from 'colorthief';
import { useEntityLogo } from '../../hooks/useEntityLogo';
import styles from './NodeLogo.module.scss';
import { useMemoizedDataIds } from '../../utils/dataId';
import { NODE_LOGO_DATA_ID_CONTRACTS } from './nodeLogoDataIds';
import { useCloudinaryOptimisedImage } from '../../hooks/useCloudinaryOptimisedImage';

export interface NodeLogoProps {
  logoURL: string;
  darkModeLogoURL: string;
  roundedBorders?: boolean;
  disabled?: boolean;
  size?: number | string;
  title?: string;
  className?: string;
  withContextualBg?: boolean;
  dataId?: string;
}

export function NodeLogo({
  logoURL,
  darkModeLogoURL,
  roundedBorders,
  disabled,
  size = 5,
  title,
  className,
  withContextualBg,
  dataId
}: NodeLogoProps) {
  const thumbnail = useEntityLogo(logoURL, darkModeLogoURL);
  const thumbnailOptimised = useCloudinaryOptimisedImage(thumbnail, 64);
  const [contextualBg, setContextualBg] = useState<string | null>(null);

  const dataIds = useMemoizedDataIds(NODE_LOGO_DATA_ID_CONTRACTS, dataId);

  const classes = clsx(
    {
      [styles.nodeLogo]: true,
      [styles[`nodeLogoSize${size}`]]: true,
      [styles.disabled]: disabled,
      [styles.nodeLogoRoundedBorders]: roundedBorders
    },
    className
  );

  const onImageLoad: ReactEventHandler<HTMLImageElement> = e => {
    if (!withContextualBg) {
      return;
    }

    const colorThief = new ColorThief();

    const rgb = colorThief.getColor(e.target as HTMLImageElement);

    setContextualBg(
      rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, .1)` : 'var(--surface-bg-overlay-color)'
    );
  };

  return (
    <div
      className={classes}
      data-id={dataIds.logoContainer}
      style={withContextualBg && contextualBg ? { backgroundColor: contextualBg } : undefined}
    >
      <img
        src={thumbnailOptimised}
        alt={title}
        data-id={dataIds.logo}
        onLoad={onImageLoad}
        crossOrigin={withContextualBg ? 'anonymous' : undefined}
      />
    </div>
  );
}
